import React, { useState, useEffect, useCallback, memo } from "react";
import PropTypes from "prop-types";

const PillInput = ({
  label,
  prefix = "",
  suffix = "",
  value,
  onValueChange,
  min,
  max,
}) => {
  const addSpaceToSuffix = (suffix) => {
    if (!suffix) return "";
    return suffix.startsWith(" ") ? suffix : ` ${suffix}`;
  };

  const formattedSuffix = addSpaceToSuffix(suffix);

  const [inputValue, setInputValue] = useState("");

  // Format number with thousand separators
  const formatNumber = (num) => {
    return Number(num).toLocaleString("en-GB");
  };

  // Update input value whenever props change
  useEffect(() => {
    setInputValue(`${prefix}${formatNumber(value)}${formattedSuffix}`);
  }, [value, prefix, suffix, formattedSuffix]);

  const removePrefix = (str) =>
    prefix && str.startsWith(prefix) ? str.slice(prefix.length) : str;

  const removeSuffix = (str) =>
    formattedSuffix && str.endsWith(formattedSuffix)
      ? str.slice(0, str.length - formattedSuffix.length)
      : str;

  const removeCommas = (str) => str.replace(/,/g, "");

  const sanitizeInputValue = (input) =>
    [removePrefix, removeSuffix, removeCommas].reduce(
      (acc, fn) => fn(acc),
      input
    );

  const handleBlur = useCallback(() => {
    const sanitized = sanitizeInputValue(inputValue);
    const parsedValue = parseFloat(sanitized);

    // If input is not a valid number, reset to original value
    if (isNaN(parsedValue)) {
      setInputValue(`${prefix}${formatNumber(value)}${formattedSuffix}`);
      return;
    }

    // Clamp value between min and max
    const boundedValue = Math.min(max, Math.max(min, parsedValue));

    // Update parent state and input display
    onValueChange(boundedValue);
    setInputValue(`${prefix}${formatNumber(boundedValue)}${formattedSuffix}`);
  }, [
    inputValue,
    sanitizeInputValue,
    value,
    min,
    max,
    onValueChange,
    prefix,
    formattedSuffix,
  ]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleBlur();
      }
    },
    [handleBlur]
  );

  // Use useCallback to avoid recreating functions on each render
  const handleChange = useCallback((e) => {
    setInputValue(e.target.value);
  }, []);

  // Generate a unique ID for accessibility
  const inputId = React.useId
    ? React.useId()
    : `pill-input-${label?.replace(/\s+/g, "-").toLowerCase() || "input"}`;

  return (
    <div className="mrc-pill-container">
      {label && <label htmlFor={inputId}>{label}</label>}
      <div className="mrc-pill-input">
        <input
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
          aria-label={label || "Value input"}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
        />
      </div>
    </div>
  );
};

// PropTypes for type checking
PillInput.propTypes = {
  label: PropTypes.string,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  value: PropTypes.number.isRequired,
  onValueChange: PropTypes.func.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
};

// Use memo to prevent unnecessary re-renders
export default memo(PillInput);
